import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useMediaQuery } from 'react-responsive';

// Palette de couleurs Corporate Premium
const colors = {
  bgDark: '#121214',
  bgMedium: '#1C1C1F',
  bgLight: '#252529',
  beigeLight: '#F5EFEB',
  beigeMain: '#D4C5B0',
  beigeDark: '#A3947F',
  textGray: '#A0A0A5',
  border: 'rgba(212, 197, 176, 0.15)',
  borderGlow: 'rgba(212, 197, 176, 0.3)'
};

const DroitFinances = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeService, setActiveService] = useState(0);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [showStats, setShowStats] = useState(false);
  
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
  const isTablet = useMediaQuery({ query: '(max-width: 1024px)' });

  const services = [
    {
      id: 1,
      title: 'Levées de Fonds',
      description: 'Structuration juridique des opérations de financement et émissions de titres',
      icon: '🏛️',
      features: [
        'Financement par actions',
        'Obligations convertibles',
        'Financement participatif',
        'Private equity'
      ]
    },
    {
      id: 2,
      title: 'Conformité Réglementaire',
      description: 'Respect des obligations imposées par les autorités financières nationales et internationales',
      icon: '⚖️',
      features: [
        'MIFID II',
        'RGPD financier',
        'AML/KYC',
        'Reporting réglementaire'
      ]
    },
    {
      id: 3,
      title: 'Contrôles & Litiges',
      description: 'Assistance lors de contrôles ou de litiges avec les autorités de régulation financière',
      icon: '🛡️',
      features: [
        'Défense AMF',
        'Contentieux bancaire',
        'Négociations amiables',
        'Représentation juridique'
      ]
    },
    {
      id: 4,
      title: 'Ingénierie Financière',
      description: 'Conception de structures financières complexes et optimisation fiscale',
      icon: '📈',
      features: [
        'Structured finance',
        'Titrisation',
        'Produits dérivés',
        'Ingénierie fiscale'
      ]
    }
  ];

  const expertiseAreas = [
    { 
      src: '/images/confo.jpg', 
      title: 'Conformité réglementaire',
      description: 'Expertise en réglementation financière internationale',
      stats: '100%'
    },
    { 
      src: '/images/strat.webp', 
      title: 'Stratégies financières',
      description: 'Conseil en structuration financière optimisée',
      stats: '150+'
    },
    { 
      src: '/images/audit.webp', 
      title: 'Audit financier',
      description: 'Audits de conformité et due diligence',
      stats: 'ISO 9001'
    },
    { 
      src: '/images/reg.avif', 
      title: 'Régulation bancaire',
      description: 'Expertise en réglementation bancaire et prudentielle',
      stats: '500+'
    },
  ];

  const processSteps = [
    { number: '01', title: 'Diagnostic', description: 'Analyse approfondie de vos besoins' },
    { number: '02', title: 'Stratégie', description: 'Élaboration d\'une approche sur mesure' },
    { number: '03', title: 'Implémentation', description: 'Mise en œuvre des solutions' },
    { number: '04', title: 'Suivi', description: 'Accompagnement continu et ajustements' }
  ];

  // Calcul des styles responsives
  const computedStyles = useMemo(() => {
    return {
      section: {
        position: 'relative',
        background: `linear-gradient(180deg, ${colors.bgDark} 0%, #17171A 50%, ${colors.bgDark} 100%)`,
        color: colors.beigeLight,
        minHeight: '100vh',
        overflow: 'hidden',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      },
      
      animatedBackground: {
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        pointerEvents: 'none', zIndex: 1, overflow: 'hidden',
      },
      
      gradientSphere: {
        position: 'absolute', top: '10%', right: '-10%',
        width: '50vw', height: '50vw',
        background: `radial-gradient(circle, rgba(212, 197, 176, 0.03) 0%, transparent 60%)`,
        filter: 'blur(80px)',
      },
      
      gradientSphere2: {
        position: 'absolute', bottom: '10%', left: '-10%',
        width: '40vw', height: '40vw',
        background: `radial-gradient(circle, rgba(212, 197, 176, 0.02) 0%, transparent 60%)`,
        filter: 'blur(80px)',
      },
      
      noiseOverlay: {
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.04'/%3E%3C/svg%3E")`,
        pointerEvents: 'none', zIndex: 1,
      },
      
      hero: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '6rem 1.5rem' : isTablet ? '8rem 3rem' : '10rem 5%',
        maxWidth: '1440px', margin: '0 auto',
      },
      
      heroContent: {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1fr',
        gap: isMobile ? '3rem' : '5rem',
        alignItems: 'center',
      },
      
      titleLine: {
        height: '1px',
        background: `linear-gradient(90deg, ${colors.beigeMain}, transparent)`,
        marginBottom: isMobile ? '1.5rem' : '2rem',
        width: isMobile ? '60px' : '100px',
      },
      
      mainTitle: {
        fontSize: isMobile ? '2.5rem' : isTablet ? '3.2rem' : '4.5rem',
        lineHeight: 1.1,
        fontWeight: 300, // Plus fin = Plus premium
        marginBottom: isMobile ? '1rem' : '1.5rem',
        color: colors.beigeLight,
        letterSpacing: '-1px',
      },
      
      subtitle: {
        fontSize: isMobile ? '1.2rem' : isTablet ? '1.4rem' : '1.8rem',
        color: colors.beigeMain,
        fontWeight: 400,
        fontStyle: 'italic',
        display: 'block',
        marginTop: isMobile ? '0.5rem' : '1rem',
        letterSpacing: '0',
      },
      
      heroTextWrapper: {
        display: 'flex', flexDirection: 'column', gap: '1.5rem',
        marginBottom: '3rem',
      },
      
      heroText: {
        fontSize: isMobile ? '1rem' : '1.1rem',
        lineHeight: 1.8,
        color: colors.textGray,
        position: 'relative',
        paddingLeft: '1.5rem',
        borderLeft: `1px solid ${colors.border}`,
        fontWeight: 300,
      },
      
      statsContainer: {
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: '1.5rem',
        paddingTop: '2rem',
        borderTop: `1px solid ${colors.border}`,
      },
      
      statCard: {
        display: 'flex', flexDirection: 'column', gap: '0.5rem',
      },
      
      statNumber: {
        fontSize: isMobile ? '1.8rem' : '2.2rem',
        fontWeight: 300,
        color: colors.beigeMain,
        fontFamily: "'Playfair Display', serif",
      },
      
      statLabel: {
        fontSize: '0.75rem',
        color: colors.textGray,
        textTransform: 'uppercase',
        letterSpacing: '1px',
      },
      
      heroImageContainer: {
        position: 'relative',
        padding: '20px', // Espace pour le cadre décalé
      },
      
      imageBackground: {
        position: 'absolute',
        top: '20px', left: '20px', right: '-20px', bottom: '-20px',
        border: `1px solid ${colors.beigeDark}`,
        borderRadius: '2px',
        zIndex: -1,
      },
      
      imageWrapper: {
        position: 'relative',
        height: isMobile ? '400px' : isTablet ? '500px' : '650px',
        borderRadius: '2px',
        overflow: 'hidden',
      },
      
      image: {
        width: '100%', height: '100%',
        objectFit: 'cover',
        filter: 'grayscale(30%) contrast(1.1) brightness(0.9)',
      },
      
      imageOverlay: {
        position: 'absolute', inset: 0,
        background: `linear-gradient(to top, rgba(18,18,20,0.8) 0%, transparent 40%)`,
      },
      
      imageBadge: {
        position: 'absolute',
        bottom: '0', left: '0',
        background: colors.bgMedium,
        padding: '1.2rem 2rem',
        border: `1px solid ${colors.border}`,
        display: 'flex', alignItems: 'center', gap: '1rem',
        zIndex: 2,
      },
      
      badgeText: {
        color: colors.beigeMain,
        fontSize: '0.9rem',
        fontWeight: 500,
        letterSpacing: '1px',
        textTransform: 'uppercase',
      },
      
      // Services Section
      servicesSection: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '4rem 1.5rem' : '8rem 5%',
        background: colors.bgDark,
        borderTop: `1px solid ${colors.border}`,
      },
      
      sectionHeader: {
        textAlign: 'center',
        marginBottom: isMobile ? '3rem' : '5rem',
      },
      
      sectionTitle: {
        fontSize: isMobile ? '2rem' : '3rem',
        fontWeight: 300,
        color: colors.beigeLight,
        marginBottom: '1rem',
        letterSpacing: '-0.5px',
      },
      
      titleHighlight: {
        color: colors.beigeMain,
        fontStyle: 'italic',
      },
      
      sectionSubtitle: {
        fontSize: '1.1rem',
        color: colors.textGray,
        maxWidth: '600px',
        margin: '0 auto',
        fontWeight: 300,
      },
      
      servicesGrid: {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
        gap: '2rem',
        maxWidth: '1200px',
        margin: '0 auto',
      },
      
      serviceCard: {
        background: colors.bgMedium,
        borderRadius: '2px',
        padding: isMobile ? '2rem' : '3rem',
        border: `1px solid ${colors.border}`,
        transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        display: 'flex', flexDirection: 'column', height: '100%',
        cursor: 'default',
      },
      
      serviceHeader: {
        display: 'flex', alignItems: 'center', gap: '1.5rem',
        marginBottom: '1.5rem',
        paddingBottom: '1.5rem',
        borderBottom: `1px solid ${colors.border}`,
      },
      
      serviceIcon: {
        fontSize: '2rem',
        color: colors.beigeMain,
      },
      
      serviceTitle: {
        fontSize: '1.4rem',
        fontWeight: 400,
        color: colors.beigeLight,
      },
      
      serviceDescription: {
        fontSize: '1rem',
        color: colors.textGray,
        lineHeight: 1.6,
        marginBottom: '2rem',
        flex: 1,
        fontWeight: 300,
      },
      
      serviceFeatures: {
        listStyle: 'none', padding: 0, margin: 0,
        display: 'flex', flexDirection: 'column', gap: '0.8rem',
      },
      
      serviceFeature: {
        fontSize: '0.9rem',
        color: colors.beigeLight,
        display: 'flex', alignItems: 'center', gap: '0.8rem',
        fontWeight: 300,
      },
      
      featureIcon: {
        color: colors.beigeDark,
        fontSize: '1rem',
      },
      
      // Gallery Section
      gallerySection: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '4rem 1.5rem' : '8rem 5%',
        background: colors.bgLight,
      },
      
      galleryGrid: {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: '1.5rem',
        maxWidth: '1440px', margin: '0 auto',
      },
      
      galleryCard: {
        position: 'relative',
        background: colors.bgMedium,
        border: `1px solid ${colors.border}`,
        borderRadius: '2px',
        overflow: 'hidden',
        minHeight: '400px',
        display: 'flex', flexDirection: 'column',
      },
      
      imageContainer: {
        position: 'relative', height: '220px', overflow: 'hidden',
      },
      
      cardContent: {
        padding: '2rem', flex: 1,
        display: 'flex', flexDirection: 'column',
      },
      
      cardTitle: {
        fontSize: '1.2rem', fontWeight: 400, color: colors.beigeLight,
        marginBottom: '0.8rem',
      },
      
      cardDescription: {
        fontSize: '0.95rem', color: colors.textGray, lineHeight: 1.6,
        marginBottom: '1.5rem', flex: 1, fontWeight: 300,
      },
      
      cardStats: {
        fontSize: '1.8rem', fontWeight: 300, color: colors.beigeMain,
        textAlign: 'right', marginTop: 'auto', fontFamily: "'Playfair Display', serif",
      },
      
      // Process Section
      processSection: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '4rem 1.5rem' : '8rem 5%',
        background: colors.bgDark,
        borderTop: `1px solid ${colors.border}`,
      },
      
      processSteps: {
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        position: 'relative',
        gap: isMobile ? '3rem' : 0,
        maxWidth: '1200px', margin: '4rem auto 0',
      },
      
      processConnector: {
        position: 'absolute',
        top: '40px', left: '10%', right: '10%', height: '1px',
        background: colors.border, zIndex: 1,
        display: isMobile ? 'none' : 'block',
      },
      
      processStep: {
        position: 'relative', zIndex: 2, textAlign: 'center', flex: 1,
      },
      
      stepCircle: {
        width: '80px', height: '80px', borderRadius: '50%',
        background: colors.bgDark,
        border: `1px solid ${colors.beigeMain}`,
        margin: '0 auto 1.5rem',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '1.2rem', fontWeight: '400', color: colors.beigeMain,
        fontFamily: "'Playfair Display', serif",
      },
      
      stepTitle: {
        fontSize: '1.1rem', fontWeight: 400, color: colors.beigeLight,
        marginBottom: '0.5rem',
      },
      
      stepDescription: {
        fontSize: '0.9rem', color: colors.textGray, fontWeight: 300,
        maxWidth: '200px', margin: '0 auto',
      },
    };
  }, [isMobile, isTablet]);

  // Animations Premium (Courbes de Bézier douces)
  const premiumTransition = { duration: 1, ease: [0.16, 1, 0.3, 1] };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.1 }
    }
  };

  const fadeUpVariants = {
    hidden: { y: 40, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: premiumTransition }
  };

  useEffect(() => {
    const timer = setTimeout(() => setShowStats(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>Droit Financier | KMJ & Associés</title>
        <meta name="description" content="Expertise en droit financier : levées de fonds, conformité réglementaire, contrôles & litiges." />
      </Helmet>

      <motion.section 
        style={computedStyles.section}
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Animated Background Minimaliste */}
        <div style={computedStyles.animatedBackground}>
          <div style={computedStyles.gradientSphere} />
          <div style={computedStyles.gradientSphere2} />
          <div style={computedStyles.noiseOverlay} />
        </div>

        {/* Hero Section */}
        <div style={computedStyles.hero}>
          <motion.div style={computedStyles.heroContent} variants={containerVariants}>
            <motion.div variants={fadeUpVariants}>
              <div style={computedStyles.titleLine} />
              <motion.h1 style={computedStyles.mainTitle}>
                Droit Financier
                <span style={computedStyles.subtitle}>
                  Excellence & Innovation.
                </span>
              </motion.h1>
              
              <motion.div style={computedStyles.heroTextWrapper}>
                <p style={computedStyles.heroText}>
                  Le cabinet KMJ & Associés accompagne ses clients dans la structuration juridique 
                  de leurs opérations financières, que ce soit pour des levées de fonds, des émissions 
                  de titres, ou la mise en place de mécanismes de financement complexes.
                </p>
                <p style={computedStyles.heroText}>
                  Nous veillons également à la conformité réglementaire stricte imposée par les 
                  autorités nationales et internationales, sécurisant ainsi vos intérêts stratégiques.
                </p>
              </motion.div>

              {/* Stats */}
              <motion.div style={computedStyles.statsContainer}>
                {[
                  
                  { number: '200+', label: 'Opérations' },
                  { number: '98%', label: 'Taux de succès' },
                  { number: 'ISO 27001', label: 'Sécurité' },
                ].map((stat, index) => (
                  <motion.div key={index} style={computedStyles.statCard}>
                    <motion.div 
                      style={computedStyles.statNumber}
                      initial={{ opacity: 0 }} animate={{ opacity: showStats ? 1 : 0 }}
                      transition={{ duration: 1.5, delay: 0.5 + (index * 0.2) }}
                    >
                      {stat.number}
                    </motion.div>
                    <div style={computedStyles.statLabel}>{stat.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Image Hero avec cadre décalé premium */}
            <motion.div style={computedStyles.heroImageContainer} variants={fadeUpVariants}>
              <div style={computedStyles.imageBackground} />
              <motion.div style={computedStyles.imageWrapper}>
                <img
                  src="/images/L4.jpg" // Image corporate (bureau, buildings, etc)
                  alt="Expertise Financière"
                  style={computedStyles.image}
                  loading="lazy"
                />
                <div style={computedStyles.imageOverlay} />
                <div style={computedStyles.imageBadge}>
                  <span style={computedStyles.badgeText}>Expertise Certifiée</span>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>

        {/* Services Section */}
        <div style={computedStyles.servicesSection}>
          <div style={computedStyles.sectionHeader}>
            <motion.h2 style={computedStyles.sectionTitle} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
              Nos <span style={computedStyles.titleHighlight}>Services</span> Spécialisés
            </motion.h2>
            <motion.p style={computedStyles.sectionSubtitle} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
              Une expertise complète pour sécuriser vos opérations et optimiser votre stratégie financière.
            </motion.p>
          </div>
          
          <div style={computedStyles.servicesGrid}>
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                style={computedStyles.serviceCard}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { opacity: 1, y: 0, transition: { ...premiumTransition, delay: index * 0.1 } }
                }}
                whileHover={{ y: -5, borderColor: colors.beigeDark, boxShadow: `0 10px 30px rgba(0,0,0,0.5)` }}
              >
                <div style={computedStyles.serviceHeader}>
                  <div style={computedStyles.serviceIcon}>{service.icon}</div>
                  <h3 style={computedStyles.serviceTitle}>{service.title}</h3>
                </div>
                <p style={computedStyles.serviceDescription}>{service.description}</p>
                <ul style={computedStyles.serviceFeatures}>
                  {service.features.map((feature, i) => (
                    <li key={i} style={computedStyles.serviceFeature}>
                      <span style={computedStyles.featureIcon}>✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Gallery Section */}
        <div style={computedStyles.gallerySection}>
          <div style={computedStyles.sectionHeader}>
            <motion.h2 style={computedStyles.sectionTitle} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
              Domaines <span style={computedStyles.titleHighlight}>d'Expertise</span>
            </motion.h2>
          </div>
          
          <div style={computedStyles.galleryGrid}>
            {expertiseAreas.map((item, index) => (
              <motion.div key={index} style={computedStyles.galleryCard}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { ...premiumTransition, delay: index * 0.1 } } }}
                whileHover={{ y: -5, borderColor: colors.beigeDark }}
              >
                <div style={computedStyles.imageContainer}>
                  <img src={item.src} alt={item.title} style={computedStyles.image} loading="lazy"/>
                  <div style={computedStyles.imageOverlay} />
                </div>
                <div style={computedStyles.cardContent}>
                  <h3 style={computedStyles.cardTitle}>{item.title}</h3>
                  <p style={computedStyles.cardDescription}>{item.description}</p>
                  <div style={computedStyles.cardStats}>{item.stats}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Process Section */}
        <div style={computedStyles.processSection}>
          <div style={computedStyles.sectionHeader}>
            <motion.h2 style={computedStyles.sectionTitle} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
              Notre <span style={computedStyles.titleHighlight}>Processus</span>
            </motion.h2>
          </div>

          <div style={computedStyles.processSteps}>
            <div style={computedStyles.processConnector} />
            {processSteps.map((step, index) => (
              <motion.div key={index} style={computedStyles.processStep}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { ...premiumTransition, delay: index * 0.2 } } }}
              >
                <div style={computedStyles.stepCircle}>{step.number}</div>
                <h3 style={computedStyles.stepTitle}>{step.title}</h3>
                <p style={computedStyles.stepDescription}>{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

      </motion.section>
    </>
  );
};

export default DroitFinances;